import {
  Page,
  Layout,
  Card,
  Button,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Tabs,
  IndexTable,
  Pagination,
  TextField,
  Spinner,
  Banner,
  useIndexResourceState,
} from '@shopify/polaris';
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createApiUrl } from '../config/api';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { useApiClient, ApiError } from '../hooks/useApiClient';

interface Label {
  _id?: string;
  id?: string;
  name: string;
  text: string;
  type: string;
  enabled: boolean;
  priority: number;
  createdAt: string;
  schedule?: any;
}

interface DashboardStats {
  totalLabels: number;
  activeLabels: number;
  totalBadges: number;
  activeBadges: number;
}

interface ShopInfo {
  name: string;
  email: string;
  domain: string;
  myshopifyDomain: string;
  plan: string;
  owner: string;
  country: string;
  currency: string;
  timezone: string;
}

export default function Dashboard() {
  const navigate = useAppNavigate();
  const [searchParams] = useSearchParams();
  const shop = searchParams.get('shop') || '';
  const api = useApiClient();

  const [stats, setStats] = useState<DashboardStats>({
    totalLabels: 0,
    activeLabels: 0,
    totalBadges: 0,
    activeBadges: 0,
  });
  const [labels, setLabels] = useState<Label[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Theme extension status
  const [themeExtensionActive, setThemeExtensionActive] = useState(false);
  const [extensionStatusLoading, setExtensionStatusLoading] = useState(true);
  const [extensionThemeName, setExtensionThemeName] = useState('');

  // Shop info
  const [shopInfo, setShopInfo] = useState<ShopInfo | null>(null);

  // Auth / session error
  const [authError, setAuthError] = useState<{ message: string; reinstallUrl?: string } | null>(null);
  const [tokenRevoked, setTokenRevoked] = useState(false);

  useEffect(() => {
    fetchData();
    fetchExtensionStatus();
    fetchShopInfo();
  }, [shop]);

  const handleApiError = useCallback((error: unknown, context?: string) => {
    if (error instanceof ApiError) {
      if (error.code === 'TOKEN_REVOKED') {
        setTokenRevoked(true);
        setAuthError({
          message: error.message,
          reinstallUrl: error.reinstallUrl,
        });
        return;
      }
      if (error.status === 401) {
        setAuthError({
          message: `Authentication failed${context ? ` (${context})` : ''}. Please reinstall the app.`,
          reinstallUrl: error.reinstallUrl,
        });
      }
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const labelsData = await api.get<Label[]>(createApiUrl('api/labels'));
      setLabels(labelsData || []);

      const allLabels = labelsData || [];
      const labelTypes = allLabels.filter((l) => l.type === 'text' || l.type === 'image');
      const badgeTypes = allLabels.filter((l) => l.type === 'badge');

      setStats({
        totalLabels: labelTypes.length || allLabels.length,
        activeLabels: labelTypes.filter((l) => l.enabled).length || allLabels.filter((l) => l.enabled).length,
        totalBadges: badgeTypes.length,
        activeBadges: badgeTypes.filter((l) => l.enabled).length,
      });
    } catch (error) {
      handleApiError(error);
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExtensionStatus = async () => {
    setExtensionStatusLoading(true);
    try {
      const data = await api.get<{
        isActive: boolean;
        themeId?: number;
        themeName?: string;
        blockId?: string | null;
        blockFound?: boolean;
        error?: string;
      }>(createApiUrl('api/theme-extension-status'));
      setThemeExtensionActive(data.isActive ?? false);
      setExtensionThemeName(data.themeName || '');
    } catch (error) {
      handleApiError(error);
      console.error('Error fetching extension status:', error);
      setThemeExtensionActive(false);
    } finally {
      setExtensionStatusLoading(false);
    }
  };

  const fetchShopInfo = async () => {
    try {
      const data = await api.get<ShopInfo>(createApiUrl('api/shop-info'));
      setShopInfo(data);
    } catch (error) {
      handleApiError(error);
      console.error('Error fetching shop info:', error);
    }
  };

  const deleteLabel = async (id: string) => {
    if (!confirm('Are you sure you want to delete this label?')) return;
    try {
      await api.delete(createApiUrl(`api/labels/${id}`));
      setLabels(labels.filter((l) => (l._id || l.id) !== id));
    } catch (error) {
      console.error('Error deleting label:', error);
    }
  };

  const toggleLabel = async (id: string, enabled: boolean) => {
    try {
      await api.patch(createApiUrl(`api/labels/${id}`), { enabled: !enabled });
      setLabels(
        labels.map((l) =>
          (l._id || l.id) === id ? { ...l, enabled: !enabled } : l
        )
      );
    } catch (error) {
      console.error('Error toggling label:', error);
    }
  };

  const duplicateLabel = async (id: string) => {
    try {
      const newLabel = await api.post<Label>(createApiUrl(`api/labels/${id}/duplicate`), {});
      setLabels((prev) => [newLabel, ...prev]);
    } catch (error) {
      console.error('Error duplicating label:', error);
    }
  };

  const bulkDelete = async (ids: string[]) => {
    if (!confirm(`Delete ${ids.length} label(s)?`)) return;
    try {
      await api.post(createApiUrl('api/labels/bulk-delete'), { ids });
      setLabels((prev) => prev.filter((l) => !ids.includes((l._id || l.id) as string)));
    } catch (error) {
      console.error('Error bulk deleting:', error);
    }
  };

  const bulkToggle = async (ids: string[], enabled: boolean) => {
    try {
      await api.post(createApiUrl('api/labels/bulk-toggle'), { ids, enabled });
      setLabels((prev) =>
        prev.map((l) => (ids.includes((l._id || l.id) as string) ? { ...l, enabled } : l))
      );
    } catch (error) {
      console.error('Error bulk toggling:', error);
    }
  };

  // Filter labels by tab and search
  const filteredLabels = labels.filter((label) => {
    if (activeTab === 1 && label.type === 'badge') return false;
    if (activeTab === 2 && label.type !== 'badge') return false;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        label.name.toLowerCase().includes(q) ||
        label.text.toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredLabels.length / pageSize);
  const paginatedLabels = filteredLabels.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const resourceName = { singular: 'label', plural: 'labels' };
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(
      paginatedLabels.map((l) => ({ id: (l._id || l.id) as string }))
    );

  const tabs = [
    { id: 'all', content: 'All', accessibilityLabel: 'All labels and badges' },
    { id: 'labels', content: 'Labels' },
    { id: 'badges', content: 'Badges' },
  ];

  const formatTimeAgo = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 30) return `${diffDays} days ago`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return '1 month ago';
    return `${diffMonths} months ago`;
  };

  const promotedBulkActions = [
    {
      content: 'Publish',
      onAction: () => bulkToggle(selectedResources, true),
    },
    {
      content: 'Unpublish',
      onAction: () => bulkToggle(selectedResources, false),
    },
  ];
  const bulkActions = [
    {
      content: 'Delete',
      onAction: () => bulkDelete(selectedResources),
      destructive: true,
    },
  ];

  const rowMarkup = paginatedLabels.map((label, index) => {
    const labelId = (label._id || label.id) as string;
    return (
      <IndexTable.Row
        id={labelId}
        key={labelId}
        selected={selectedResources.includes(labelId)}
        position={index}
        onClick={() => navigate(`/labels/${labelId}`)}
      >
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="bold" as="span">
            {label.name || 'Untitled'}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Badge tone={label.type === 'badge' ? 'info' : undefined}>
            {label.type === 'badge' ? 'Badge' : 'Label'}
          </Badge>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Badge tone={label.enabled ? 'success' : 'warning'}>
            {label.enabled ? 'Published' : 'Draft'}
          </Badge>
        </IndexTable.Cell>
        <IndexTable.Cell>
          {formatTimeAgo(label.createdAt)}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <InlineStack gap="200">
            <Button
              size="slim"
              onClick={() => navigate(`/labels/${labelId}`)}
            >
              Edit
            </Button>
            <Button
              size="slim"
              onClick={() => duplicateLabel(labelId)}
            >
              Duplicate
            </Button>
            <Button
              size="slim"
              tone={label.enabled ? 'critical' : undefined}
              onClick={() => toggleLabel(labelId, label.enabled)}
            >
              {label.enabled ? 'Unpublish' : 'Publish'}
            </Button>
            <Button
              size="slim"
              tone="critical"
              variant="plain"
              onClick={() => deleteLabel(labelId)}
            >
              Delete
            </Button>
          </InlineStack>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  if (loading) {
    return (
      <Page title="Product Labels & Badges">
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack align="center">
                <Spinner size="large" />
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title="Product Labels & Badges">
      <Layout>
        {/* Auth error / token revoked banner */}
        {authError && (
          <Layout.Section>
            <Banner
              title={tokenRevoked ? 'App needs to be reinstalled' : 'Session expired'}
              tone="critical"
              action={{
                content: 'Reinstall App Now',
                url: authError.reinstallUrl
                  || `https://apilabely.esire.org/api/auth?shop=${encodeURIComponent(shop)}`,
              }}
              onDismiss={() => setAuthError(null)}
            >
              <p>
                {tokenRevoked
                  ? 'The access token stored for this store is no longer valid — it was revoked when the app was previously uninstalled. The stale session has been cleared. Click "Reinstall App Now" to go through the OAuth flow and create a fresh connection.'
                  : (authError.message || 'Your session is no longer valid. Please reinstall the app.')}
              </p>
            </Banner>
          </Layout.Section>
        )}

        {/* Theme Extension Status Bar */}
        <Layout.Section>
          <Card>
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="200">
                <InlineStack gap="200" blockAlign="center">
                  <Text as="h2" variant="headingMd" fontWeight="bold">
                    Theme extension
                  </Text>
                  {extensionStatusLoading ? (
                    <Badge>Checking...</Badge>
                  ) : tokenRevoked ? (
                    <Badge tone="critical">Session Invalid</Badge>
                  ) : (
                    <Badge tone={themeExtensionActive ? 'success' : undefined}>
                      {themeExtensionActive ? 'Active' : 'Inactive'}
                    </Badge>
                  )}
                </InlineStack>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {tokenRevoked
                    ? 'Cannot check status — the app session is invalid. Reinstall the app to restore access.'
                    : themeExtensionActive && extensionThemeName
                    ? `Enabled on theme "${extensionThemeName}". Your labels are visible to customers.`
                    : 'Embed the app into your theme to get access to our features. Shopify Theme Extension doesn\'t directly affect your theme code.'}
                </Text>
              </BlockStack>
              <InlineStack gap="200">
                <Button onClick={() => fetchExtensionStatus()} loading={extensionStatusLoading}>
                  Refresh
                </Button>
                <Button
                  variant="primary"
                  url={shop ? `https://admin.shopify.com/store/${shop.replace('.myshopify.com', '')}/themes/current/editor?context=apps` : '#'}
                  external
                >
                  {themeExtensionActive ? 'Manage' : 'Activate'}
                </Button>
              </InlineStack>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Active Labels & Badges Summary Cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <div style={{ flex: 1 }}>
              <Card>
                <InlineStack gap="400" blockAlign="center">
                  <div style={{ fontSize: '48px', lineHeight: 1 }}>🏷️</div>
                  <BlockStack gap="200">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="h3" variant="headingMd" fontWeight="bold">
                        Active labels
                      </Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {stats.activeLabels} / {stats.totalLabels}
                      </Text>
                    </InlineStack>
                    <Text as="p" variant="bodyMd" tone="subdued">
                      Style your products with a label that sells
                    </Text>
                    <Button variant="primary" onClick={() => navigate('/labels/new')}>
                      Create Label
                    </Button>
                  </BlockStack>
                </InlineStack>
              </Card>
            </div>
            <div style={{ flex: 1 }}>
              <Card>
                <InlineStack gap="400" blockAlign="center">
                  <div style={{ fontSize: '48px', lineHeight: 1 }}>🏅</div>
                  <BlockStack gap="200">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="h3" variant="headingMd" fontWeight="bold">
                        Active badges
                      </Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {stats.activeBadges} / {stats.totalBadges}
                      </Text>
                    </InlineStack>
                    <Text as="p" variant="bodyMd" tone="subdued">
                      Turn your product into customer magnets
                    </Text>
                    <Button variant="primary" onClick={() => navigate('/labels/new')}>
                      Create Badge
                    </Button>
                  </BlockStack>
                </InlineStack>
              </Card>
            </div>
          </InlineStack>
        </Layout.Section>

        {/* Main content + Sidebar */}
        <Layout.Section>
          <BlockStack gap="400">
            <Text as="h2" variant="headingLg" fontWeight="bold">
              Labels & Badges
            </Text>

            {/* Warning banner if theme extension is not active */}
            {!extensionStatusLoading && !themeExtensionActive && (
              <Card>
                <InlineStack align="space-between" blockAlign="center">
                  <InlineStack gap="200" blockAlign="center">
                    <Text as="span" tone="caution">⚠</Text>
                    <Text as="p" variant="bodyMd" tone="subdued">
                      Labels and badges cannot be shown in the store because the theme extension is not activated.
                    </Text>
                  </InlineStack>
                  <Button
                    variant="plain"
                    url={shop ? `https://admin.shopify.com/store/${shop.replace('.myshopify.com', '')}/themes/current/editor?context=apps` : '#'}
                    external
                  >
                    Setup
                  </Button>
                </InlineStack>
              </Card>
            )}

            <Card padding="0">
              <BlockStack>
                {/* Tabs + Search */}
                <div style={{ padding: '12px 16px 0' }}>
                  <InlineStack align="space-between" blockAlign="center">
                    <Tabs
                      tabs={tabs}
                      selected={activeTab}
                      onSelect={(idx) => {
                        setActiveTab(idx);
                        setCurrentPage(1);
                      }}
                      fitted={false}
                    />
                    <div style={{ width: '240px' }}>
                      <TextField
                        label="Search"
                        labelHidden
                        value={searchQuery}
                        onChange={(v) => {
                          setSearchQuery(v);
                          setCurrentPage(1);
                        }}
                        placeholder="Search labels..."
                        autoComplete="off"
                        clearButton
                        onClearButtonClick={() => setSearchQuery('')}
                      />
                    </div>
                  </InlineStack>
                </div>

                {/* Table */}
                {filteredLabels.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <BlockStack gap="300" inlineAlign="center">
                      <Text as="p" variant="bodyMd" tone="subdued">
                        {searchQuery
                          ? 'No labels matching your search.'
                          : 'No labels found. Create your first label to get started!'}
                      </Text>
                      {!searchQuery && (
                        <Button variant="primary" onClick={() => navigate('/labels/new')}>
                          Create Label
                        </Button>
                      )}
                    </BlockStack>
                  </div>
                ) : (
                  <IndexTable
                    resourceName={resourceName}
                    itemCount={filteredLabels.length}
                    selectedItemsCount={
                      allResourcesSelected ? 'All' : selectedResources.length
                    }
                    onSelectionChange={handleSelectionChange}
                    headings={[
                      { title: 'Name' },
                      { title: 'Type' },
                      { title: 'Status' },
                      { title: 'Created at' },
                      { title: 'Actions' },
                    ]}
                    promotedBulkActions={promotedBulkActions}
                    bulkActions={bulkActions}
                    selectable
                  >
                    {rowMarkup}
                  </IndexTable>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ padding: '16px', display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                      hasPrevious={currentPage > 1}
                      onPrevious={() => setCurrentPage((p) => p - 1)}
                      hasNext={currentPage < totalPages}
                      onNext={() => setCurrentPage((p) => p + 1)}
                    />
                  </div>
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>

        {/* Sidebar: Shop Info */}
        <Layout.Section variant="oneThird">
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd" fontWeight="bold">
                  Store Info
                </Text>
                {shopInfo ? (
                  <BlockStack gap="200">
                    <InlineStack align="space-between">
                      <Text as="span" variant="bodySm" tone="subdued">Store</Text>
                      <Text as="span" variant="bodySm" fontWeight="semibold">{shopInfo.name}</Text>
                    </InlineStack>
                    <InlineStack align="space-between">
                      <Text as="span" variant="bodySm" tone="subdued">Owner</Text>
                      <Text as="span" variant="bodySm">{shopInfo.owner}</Text>
                    </InlineStack>
                    <InlineStack align="space-between">
                      <Text as="span" variant="bodySm" tone="subdued">Plan</Text>
                      <Badge>{shopInfo.plan}</Badge>
                    </InlineStack>
                    <InlineStack align="space-between">
                      <Text as="span" variant="bodySm" tone="subdued">Domain</Text>
                      <Text as="span" variant="bodySm">{shopInfo.domain}</Text>
                    </InlineStack>
                    <InlineStack align="space-between">
                      <Text as="span" variant="bodySm" tone="subdued">Country</Text>
                      <Text as="span" variant="bodySm">{shopInfo.country}</Text>
                    </InlineStack>
                    <InlineStack align="space-between">
                      <Text as="span" variant="bodySm" tone="subdued">Currency</Text>
                      <Text as="span" variant="bodySm">{shopInfo.currency}</Text>
                    </InlineStack>
                  </BlockStack>
                ) : (
                  <Text as="p" variant="bodySm" tone="subdued">Loading store info...</Text>
                )}
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd" fontWeight="bold">
                  Quick Links
                </Text>
                <BlockStack gap="200">
                  <Button
                    variant="plain"
                    url={shop ? `https://admin.shopify.com/store/${shop.replace('.myshopify.com', '')}/themes/current/editor?context=apps` : '#'}
                    external
                  >
                    Theme Editor
                  </Button>
                  <Button
                    variant="plain"
                    url={shop ? `https://${shop.replace('.myshopify.com', '')}.myshopify.com` : '#'}
                    external
                  >
                    Visit Storefront
                  </Button>
                  <Button
                    variant="plain"
                    url="https://danishnaseerktk.com"
                    external
                  >
                    Support & Docs
                  </Button>
                </BlockStack>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
